// @ts-check
import { useRef, useState } from "react"
import { Undo2, Redo2, X, Loader2, FileText, MoreHorizontal, MoreVertical, Heading1, Heading2, Heading3, List, ListOrdered, Sigma, Trash2, Download, Printer } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import ExportMenu from "./ExportMenu"
import { useUpdateDocument, useUndo, useRedo, useDeleteDocument, useExportDocument } from "@/services/chat-provider/canvas"
import { cn } from "@/lib/utils"
import { showConfirm } from "@minohealth/common/utils"

/** Small icon button with a tooltip — used for the dense action row. */
function ToolbarIconButton({ tooltip, onClick, disabled, className, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8 rounded-full", className)}
          disabled={disabled}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

/**
 * @param {{
 *   documentId: string,
 *   sessionId?: string,
 *   title: string,
 *   currentVersionId?: string,
 *   updatedAt?: string,
 *   canUndo: boolean,
 *   canRedo: boolean,
 *   onClose: () => void,
 *   onToggleHeading?: (level: 1 | 2 | 3) => void,
 *   onToggleBulletList?: () => void,
 *   onToggleOrderedList?: () => void,
 *   onInsertInlineMath?: () => void,
 *   onInsertBlockMath?: () => void,
 *   mockHandlers?: { onUndo: () => void, onRedo: () => void },
 *   readOnly?: boolean,
 * }} props
 */
export default function CanvasToolbar({
  documentId,
  sessionId,
  title,
  currentVersionId,
  updatedAt,
  canUndo,
  canRedo,
  onClose,
  onToggleHeading,
  onToggleBulletList,
  onToggleOrderedList,
  onInsertInlineMath,
  onInsertBlockMath,
  mockHandlers,
  readOnly = false,
}) {
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(title)
  const titleInputRef = useRef(null)

  const { mutate: updateDocument } = useUpdateDocument(documentId)
  const { mutate: undo, isPending: isUndoing } = useUndo(documentId)
  const { mutate: redo, isPending: isRedoing } = useRedo(documentId)
  const { mutate: deleteDocument, isPending: isDeleting } = useDeleteDocument(documentId, sessionId)
  const { mutate: exportDocument } = useExportDocument(documentId)

  const isActionPending = isUndoing || isRedoing

  function handleDelete() {
    showConfirm(`Delete "${title}"? This cannot be undone.`, "Delete document", "critical")
      .then(() => deleteDocument(undefined, { onSuccess: onClose }))
      .catch(() => {})
  }

  function handleMobileExport(format) {
    exportDocument(
      { format, versionId: currentVersionId },
      {
        onSuccess({ blob, filename }) {
          const url = URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = filename
          a.click()
          URL.revokeObjectURL(url)
        },
      }
    )
  }

  function handleMobilePrint() {
    const printWindow = window.open("", "_blank")
    exportDocument(
      { format: "pdf", versionId: currentVersionId },
      {
        onSuccess({ blob }) {
          if (!printWindow) return
          const url = URL.createObjectURL(blob)
          printWindow.location.href = url
          printWindow.addEventListener("load", () => printWindow.print())
        },
        onError() { printWindow?.close() },
      }
    )
  }

  function commitTitle() {
    setEditingTitle(false)
    if (titleDraft.trim() && titleDraft !== title) {
      updateDocument({ title: titleDraft.trim() })
    }
  }

  function handleTitleKeyDown(e) {
    if (e.key === "Enter") { e.preventDefault(); commitTitle() }
    if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(title) }
  }

  // Keep draft in sync when title changes externally
  if (!editingTitle && titleDraft !== title) {
    setTitleDraft(title)
  }

  const subtitle = readOnly ? "View only" : updatedAt
    ? `Saved ${formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}`
    : null

  return (
    <div className="flex items-center gap-2 px-3 sm:px-4 h-14 rounded-t-2xl border-b border-border bg-background shrink-0">
      <FileText size={16} className="text-muted-foreground shrink-0" />

      {/* Title + status subtitle */}
      <div className="flex-1 min-w-0 flex flex-col leading-tight">
        {editingTitle ? (
          <Input
            ref={titleInputRef}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={handleTitleKeyDown}
            autoFocus
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            className="h-7 w-full min-w-0 rounded border-0 border-b border-primary bg-transparent px-0.5 py-0 text-sm font-semibold shadow-none focus-visible:ring-0"
          />
        ) : (
          <button
            disabled={readOnly}
            onClick={() => { setEditingTitle(true); setTitleDraft(title) }}
            className="min-w-0 text-sm font-semibold text-left truncate bg-transparent hover:text-primary transition-colors"
            title={readOnly ? undefined : "Click to rename"}
          >
            {title}
          </button>
        )}
        {subtitle && !editingTitle && (
          <span className="text-[11px] text-muted-foreground truncate">{subtitle}</span>
        )}
      </div>

      {/* Mobile: single kebab menu with every tool flattened into one list */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full shrink-0 sm:hidden" title="Tools">
            <MoreVertical size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="sm:hidden">
          {!readOnly && (
            <>
              <DropdownMenuItem disabled={!canUndo || isActionPending} onClick={() => mockHandlers ? mockHandlers.onUndo() : undo()}>
                <Undo2 size={14} className="mr-1.5" /> Undo
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canRedo || isActionPending} onClick={() => mockHandlers ? mockHandlers.onRedo() : redo()}>
                <Redo2 size={14} className="mr-1.5" /> Redo
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onToggleHeading?.(1)}>
                <Heading1 size={14} className="mr-1.5" /> Heading 1
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleHeading?.(2)}>
                <Heading2 size={14} className="mr-1.5" /> Heading 2
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleHeading?.(3)}>
                <Heading3 size={14} className="mr-1.5" /> Heading 3
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleBulletList?.()}>
                <List size={14} className="mr-1.5" /> Bullet list
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleOrderedList?.()}>
                <ListOrdered size={14} className="mr-1.5" /> Numbered list
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onInsertInlineMath?.()}>
                <Sigma size={14} className="mr-1.5" /> Insert inline equation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onInsertBlockMath?.()}>
                <Sigma size={14} className="mr-1.5" /> Insert block equation
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => handleMobileExport("pdf")}>
            <Download size={14} className="mr-1.5" /> Export as PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleMobileExport("latex")}>
            <Download size={14} className="mr-1.5" /> Export as LaTeX
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleMobilePrint}>
            <Printer size={14} className="mr-1.5" /> Print
          </DropdownMenuItem>
          {!readOnly && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                <Trash2 size={14} className="mr-1.5" /> Delete document
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={onClose}>
            <X size={14} className="mr-1.5" /> Close canvas
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Desktop: editing tools, with Close set apart from them */}
      <div className="hidden sm:flex items-center gap-1.5 shrink-0">
        <div className="flex items-center gap-0.5 rounded-full border border-border bg-muted/40 px-1 py-1">
          {!readOnly && (
            <>
              <ToolbarIconButton tooltip="Undo" disabled={!canUndo || isActionPending} onClick={() => mockHandlers ? mockHandlers.onUndo() : undo()}>
                {isUndoing ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
              </ToolbarIconButton>
              <ToolbarIconButton tooltip="Redo" disabled={!canRedo || isActionPending} onClick={() => mockHandlers ? mockHandlers.onRedo() : redo()}>
                {isRedoing ? <Loader2 size={14} className="animate-spin" /> : <Redo2 size={14} />}
              </ToolbarIconButton>

              <Separator orientation="vertical" className="h-5 mx-0.5" />
            </>
          )}
          <ExportMenu documentId={documentId} currentVersionId={currentVersionId} />

          {/* Formatting, plus the actions that should not sit next to Close */}
          {!readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" aria-label="More tools" title="More tools">
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onToggleHeading?.(1)}>
                  <Heading1 size={14} className="mr-1.5" /> Heading 1
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleHeading?.(2)}>
                  <Heading2 size={14} className="mr-1.5" /> Heading 2
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleHeading?.(3)}>
                  <Heading3 size={14} className="mr-1.5" /> Heading 3
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onToggleBulletList?.()}>
                  <List size={14} className="mr-1.5" /> Bullet list
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleOrderedList?.()}>
                  <ListOrdered size={14} className="mr-1.5" /> Numbered list
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onInsertInlineMath?.()}>
                  <Sigma size={14} className="mr-1.5" /> Insert inline equation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onInsertBlockMath?.()}>
                  <Sigma size={14} className="mr-1.5" /> Insert block equation
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={isDeleting} onClick={handleDelete} className="text-destructive">
                  <Trash2 size={14} className="mr-1.5" /> Delete document
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
              aria-label="Close document"
              onClick={onClose}
            >
              <X size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Close document</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
